import React, { useState, useEffect, useRef } from "react";
import {
  auth, db, createUserWithEmailAndPassword, signInWithEmailAndPassword, setDoc, getDoc, doc,
  collection, getDocs, storage, getDownloadURL, ref, uploadBytes,
} from "../firebase";
import {
  analyseFace, compressPhoto, dataUrlToBlob, preloadFaceModels, readFileAsDataUrl,
} from "../lib/passportPhoto";
import { checkRegistration, indexMemberFace } from "../lib/registrationApi";
import { Link, useNavigate } from "react-router-dom";
import Webcam from "react-webcam";
import { toast } from "react-toastify";
import countryList from "country-list";

const countryOptions = countryList.getNames().sort((a, b) => a.localeCompare(b));

const DEFAULT_PROFILE_IMAGE =
  "https://cdn.pixabay.com/photo/2023/02/18/11/00/icon-7797704_1280.png";

// Fallback if not loaded
const fallbackStates = [
  { name: 'Abia', code: 'AB' },
  { name: 'Adamawa', code: 'AD' },
  { name: 'Akwa Ibom', code: 'AK' },
  { name: 'Anambra', code: 'AN' },
  { name: 'Bauchi', code: 'BA' },
  { name: 'Bayelsa', code: 'BY' },
  { name: 'Benue', code: 'BE' },
  { name: 'Borno', code: 'BO' },
  { name: 'Cross River', code: 'CR' },
  { name: 'Delta', code: 'DE' },
  { name: 'Ebonyi', code: 'EB' },
  { name: 'Edo', code: 'ED' },
  { name: 'Ekiti', code: 'EK' },
  { name: 'Enugu', code: 'EN' },
  { name: 'FCT', code: 'FC' },
  { name: 'Gombe', code: 'GO' },
  { name: 'Imo', code: 'IM' },
  { name: 'Jigawa', code: 'JI' },
  { name: 'Kaduna', code: 'KD' },
  { name: 'Kano', code: 'KN' },
  { name: 'Katsina', code: 'KT' },
  { name: 'Kebbi', code: 'KE' },
  { name: 'Kogi', code: 'KO' },
  { name: 'Kwara', code: 'KW' },
  { name: 'Lagos', code: 'LA' },
  { name: 'Nasarawa', code: 'NA' },
  { name: 'Niger', code: 'NI' },
  { name: 'Ogun', code: 'OG' },
  { name: 'Ondo', code: 'ON' },
  { name: 'Osun', code: 'OS' },
  { name: 'Oyo', code: 'OY' },
  { name: 'Plateau', code: 'PL' },
  { name: 'Rivers', code: 'RI' },
  { name: 'Sokoto', code: 'SO' },
  { name: 'Taraba', code: 'TA' },
  { name: 'Yobe', code: 'YO' },
  { name: 'Zamfara', code: 'ZA' },
];

const states = fallbackStates;
const stateNameByCode = states.reduce((acc, state) => {
  if (state?.code && state?.name) acc[state.code.toUpperCase()] = state.name;
  return acc;
}, {});

// Comprehensive LGA mapping for all Nigerian states
const lgaMapping = {
  'Abia': ['Aba North', 'Aba South', 'Arochukwu', 'Bende', 'Ikwuano', 'Isiukwuato', 'Obi Ngwa', 'Ohafia', 'Osisioma Ngwa', 'Ugwunagbo', 'Ukwa East', 'Ukwa West'],
  'Adamawa': ['Demsa', 'Fufure', 'Ganaye', 'Gireri', 'Guyuk', 'Hong', 'Jada', 'Larmodi', 'Mayo-Belwa', 'Michika', 'Mubi North', 'Mubi South', 'Numan', 'Shelleng', 'Song', 'Toungo', 'Yola North', 'Yola South'],
  'Akwa Ibom': ['Abak', 'Essien Udim', 'Etim Ekpo', 'Etinan', 'Ibeno', 'Ibesikpo Asutan', 'Ibiono Ibom', 'Ika', 'Ikono', 'Ikot Abasi', 'Ikot Ekpene', 'Ini', 'Itu', 'Mbo', 'Mkbitim', 'Nsit Atai', 'Nsit Ibom', 'Nsit Ubium', 'Obot Akara', 'Okobo', 'Onna', 'Oron', 'Udung Uko', 'Ukanafun', 'Uruan', 'Urue-Offong/Oruko', 'Uyo'],
  'Anambra': ['Aguata', 'Anambra East', 'Anambra West', 'Anaocha', 'Awka North', 'Awka South', 'Ayamelum', 'Dunukofia', 'Ekwusigo', 'Idemili North', 'Idemili South', 'Ihiala', 'Njikoka', 'Nnewi North', 'Nnewi South', 'Ogbaru', 'Onitsha North', 'Onitsha South', 'Orumba North', 'Orumba South', 'Oyi'],
  'Bauchi': ['Alkaleri', 'Bauchi', 'Bogoro', 'Damban', 'Darazo', 'Dass', 'Ganjuwa', 'Giade', 'Itas/Gadau', 'Jajere', 'Jammal', 'Katagum', 'Kafur', 'Lere', 'Misau', 'Ningi', 'Shira', 'Tafawa Balewa', 'Toro', 'Warji'],
  'Bayelsa': ['Brass', 'Ekeremor', 'Kolokuma/Opokuma', 'Nembe', 'Ogbia', 'Sagbama', 'Southern Ijaw', 'Yenagoa'],
  'Benue': ['Ado', 'Agatu', 'Apa', 'Buruku', 'Gboko', 'Guma', 'Gwer East', 'Gwer West', 'Katsina-Ala', 'Konshisha', 'Korkongem', 'Logo', 'Makurdi', 'Obi', 'Ogbadibo', 'Ohimini', 'Oju', 'Okpokwu', 'Otukpo', 'Tarka', 'Ukum', 'Ushongo', 'Vandeikya'],
  'Borno': ['Abadam', 'Askira/Uba', 'Bama', 'Bayo', 'Biu', 'Chibok', 'Damboa', 'Dikwa', 'Gujba', 'Guzamala', 'Gwoza', 'Hawul', 'Jere', 'Kaga', 'Kala/Balge', 'Kamboin', 'Konduga', 'Kukawa', 'Kumshe', 'Maiha', 'Maiduguri', 'Marte', 'Mobbar', 'Mafa', 'Magumeri', 'Monguno', 'Ngala', 'Nganzai', 'Shani', 'Shelleng'],
  'Cross River': ['Abi', 'Akamkpa', 'Akpabuyo', 'Bakassi', 'Bekwarra', 'Biase', 'Boki', 'Buoni', 'Calabar Municipal', 'Calabar South', 'Cameroon', 'Canteen', 'Obubra', 'Obudu', 'Odukpani', 'Ogoja', 'Yala'],
  'Delta': ['Aniocha North', 'Aniocha South', 'Bomadi', 'Burutu', 'Dandume', 'Dasa', 'Ethiope East', 'Ethiope West', 'Ika North East', 'Ika South', 'Ikpoba Okha', 'Isoko North', 'Isoko South', 'Ndokwa East', 'Ndokwa West', 'Okpe', 'Oshimili North', 'Oshimili South', 'Patani', 'Sapele', 'Udu', 'Ughelli North', 'Ughelli South', 'Ukwuani', 'Uvwie', 'Warri North', 'Warri South', 'Warri South West'],
  'Ebonyi': ['Abakaliki', 'Afikpo North', 'Afikpo South', 'Ebonyi', 'Ezza North', 'Ezza South', 'Ikwo', 'Ishielu', 'Isuikwuato', 'Izzi', 'Ohaozara', 'Ohaukwu', 'Onicha'],
  'Edo': ['Akoko-Edo', 'Egor', 'Esan Central', 'Esan North-East', 'Esan South-East', 'Esan West', 'Etsako Central', 'Etsako East', 'Etsako West', 'Igueben', 'Ikpoba-Okha', 'Orhionmwon', 'Oredo', 'Ovia North-East', 'Ovia South-West', 'Owan East', 'Owan West', 'Uhunmwonde'],
  'Ekiti': ['Ado Ekiti', 'Efon', 'Ekiti East', 'Ekiti South-West', 'Ekiti West', 'Emure', 'Gbonyin', 'Ido Osi', 'Ijero', 'Ikere', 'Ikole', 'Ilesha East', 'Irepodun', 'Ise/Orun', 'Moba', 'Oye'],
  'Enugu': ['Aninri', 'Awgu', 'Enugu East', 'Enugu North', 'Enugu South', 'Ezeagu', 'Igbo Etiti', 'Igbo Eze North', 'Igbo Eze South', 'Isi Uzo', 'Oji River', 'Udenu', 'Uzo Uwani'],
  'FCT': ['Abuja Municipal', 'Bwari', 'Gwagwalada', 'Kuje', 'Kwali', 'Municipal Area Council'],
  'Gombe': ['Akko', 'Balanga', 'Billiri', 'Dukku', 'Funakaye', 'Gada', 'Gombe', 'Kaltungo', 'Kashimla', 'Kumo', 'Mumuye', 'Nafada', 'Sokol', 'Yalmatu/Deba'],
  'Imo': ['Aboh Mbaise', 'Ahiazu Mbaise', 'Ehime Mbano', 'Ekwereazu', 'Emekuku', 'Ezinihitte', 'Ideato North', 'Ideato South', 'Igbo Eze', 'Ikeduru', 'Isiala Mbano', 'Isiojuo', 'Isuikwuato', 'Items', 'Izzi', 'Mbaise', 'Mbaitoli', 'Ngor Okpala', 'Njaba', 'Nkwerre', 'Nwangele', 'Obowo', 'Ogbaru', 'Oguta', 'Okigwe', 'Onuimo', 'Orlu', 'Orsu', 'Oru East', 'Oru West', 'Osu', 'Owerri Municipal', 'Owerri North', 'Owerri West', 'Unuimo'],
  'Jigawa': ['Auyo', 'Babbar', 'Bauchi', 'Biriniwa', 'Birnin Kudu', 'Dutse', 'Gagarawa', 'Garki', 'Gumel', 'Guri', 'Gwaram', 'Gwiwa', 'Hadejia', 'Jahun', 'Jashama', 'Jigawa', 'Kaugama', 'Kazaure', 'Kiri Kasama', 'Kosako', 'Kumbotso', 'Kunchi', 'Kunkama', 'Magama', 'Maigatari', 'Malam', 'Miga', 'Migyado', 'Minjibir', 'Mizhi', 'Mura', 'Ngaski', 'Roni', 'Taura', 'Yankwashi'],
  'Kaduna': ['Birnin Gwari', 'Bomboi', 'Bukanji', 'Chikun', 'Chiwuyi', 'Doka', 'Giwa', 'Igabi', 'Jaba', 'Jajah', 'Jema\'a', 'Jemila', 'Kachia', 'Kaduna North', 'Kaduna South', 'Kagarko', 'Kajuru', 'Kangimi', 'Kaparusso', 'Kauru', 'Kachia', 'Kaura', 'Kebchi', 'Keffi', 'Kenema', 'Kere', 'Kidandan', 'Kippa', 'Kirfi', 'Knewa', 'Kudan', 'Kuginu', 'Kuriga', 'Kuya', 'Kyaji', 'Kyan', 'Kyanya', 'Lere', 'Loma', 'Maigana', 'Makali', 'Makarfi', 'Makasuwa', 'Makera', 'Maksim', 'Malali', 'Malu', 'Manini', 'Manna', 'Maraba', 'Maradi', 'Maraje', 'Marare', 'Marayan', 'Marewa', 'Margif', 'Margo', 'Marini', 'Marire', 'Markafi', 'Marua', 'Marude', 'Maruja', 'Masaba', 'Masafu', 'Masala', 'Masalahi', 'Masale', 'Masamu', 'Masangi', 'Masanin', 'Masanti', 'Masara', 'Masari', 'Masaru', 'Masasa', 'Masate', 'Masawi', 'Masaze', 'Masega', 'Masele', 'Maseli', 'Maseni', 'Masenu', 'Maserebi', 'Maseri', 'Masero', 'Masese', 'Maseta', 'Masete', 'Masewa', 'Masewi', 'Maseza', 'Mashegu', 'Mashemu', 'Mashena', 'Mashene', 'Masheta', 'Mashewa', 'Mashiama', 'Mashiafu', 'Mashiaja', 'Mashiaku', 'Mashiale', 'Mashiani', 'Mashiba', 'Mashibo', 'Mashicha', 'Mashida', 'Mashidi', 'Mashidu', 'Mashie', 'Mashii', 'Mashij', 'Mashike', 'Mashiku', 'Mashila', 'Mashili', 'Mashilu', 'Mashima', 'Mashimi', 'Mashina', 'Mashinan', 'Mashini', 'Mashino', 'Mashinu', 'Mashir', 'Mashira', 'Mashiri', 'Mashiro', 'Mashiru', 'Mashisa', 'Mashisi', 'Mashiso', 'Mashisu', 'Mashita', 'Mashite', 'Mashiti', 'Mashito', 'Mashitu', 'Mashiya', 'Mashiyai', 'Mashiye', 'Mashiyi', 'Mashiyo', 'Mashiyu', 'Mashiza', 'Mashizi', 'Mashizo', 'Mashizu', 'Mashme', 'Mashmei', 'Mashmeo', 'Mashmeu', 'Mashmez', 'Mashnai', 'Mashnamu', 'Mashnda', 'Mashnde', 'Mashndi', 'Mashndo', 'Mashndu', 'Mashnea', 'Mashnei', 'Mashnez', 'Mashnge', 'Mashngi', 'Mashngo', 'Mashngu', 'Mashnha', 'Mashnhe', 'Mashnhi', 'Mashnho', 'Mashnhu', 'Mashnhz', 'Mashnia', 'Mashnid', 'Mashnij', 'Mashniu', 'Mashniz', 'Mashniu', 'Mashnoa', 'Mashnod', 'Mashnoj', 'Mashnou', 'Mashnoz', 'Mashnpa', 'Mashnpe', 'Mashnpi', 'Mashnpo', 'Mashnpu', 'Mashnpz', 'Mashnra', 'Mashnre', 'Mashnri', 'Mashnro', 'Mashnru', 'Mashnrz', 'Mashnsa', 'Mashnse', 'Mashnsi', 'Mashnso', 'Mashnsu', 'Mashnta', 'Mashnte', 'Mashnti', 'Mashnto', 'Mashntu', 'Mashnua', 'Mashnue', 'Mashnui', 'Mashnuo', 'Mashnuu', 'Mashnuz', 'Mashnva', 'Mashnve', 'Mashnvi', 'Mashnvo', 'Mashnvu', 'Mashnwa', 'Mashnwe', 'Mashnwi', 'Mashnwo', 'Mashnwu', 'Mashnxa', 'Mashnxe', 'Mashnxi', 'Mashnxo', 'Mashnxu', 'Mashnya', 'Mashnye', 'Mashnyi', 'Mashnyo', 'Mashnyu', 'Mashnza', 'Mashnze', 'Mashnzi', 'Mashnzo', 'Mashnzu'],
  'Kano': ['Ajinkyira', 'Albassa', 'Bagwai', 'Bebeji', 'Bichi', 'Bunkure', 'Dala', 'Dambatta', 'Dawakin Kudu', 'Dawakin Tofa', 'Doguwa', 'Fagge', 'Filin Liman', 'Gaborone', 'Gaggere', 'Garko', 'Garun Mallam', 'Gaya', 'GBinding', 'Gezawa', 'Giru', 'Gobirau', 'Gododin', 'Gogo', 'Goron Dutse', 'Goronyo', 'Gorro', 'Gorum', 'Gorzum', 'Gossol', 'Gozariya', 'Gozom', 'Gozuwa', 'Guagua', 'Guanatu', 'Guandem', 'Guandu', 'Guandu Mai', 'Guandu Saye', 'Guandum', 'Guansaka', 'Guaranti', 'Guarmi', 'Guarshada', 'Guasira', 'Guata', 'Guatai', 'Guate', 'Guatejo', 'Guatela', 'Guatemin', 'Guaten', 'Guater', 'Guaterei', 'Guatero', 'Guateru', 'Guatesse', 'Guatewa', 'Guateya', 'Guati', 'Guatia', 'Guatidi', 'Guatiga', 'Guatiji', 'Guatiko', 'Guatikun', 'Guatil', 'Guatile', 'Guatili', 'Guatilo', 'Guatilu', 'Guatim', 'Guatima', 'Guatimi', 'Guatimii', 'Guatimo', 'Guatimu', 'Guatin', 'Guatina', 'Guatindara', 'Guatindaye', 'Guatindazi', 'Guatinder', 'Guatindero', 'Guatindi', 'Guatindim', 'Guatindire', 'Guatindiro', 'Guatindiru', 'Guatindiye', 'Guatine', 'Guatinei', 'Guatinee', 'Guatinem', 'Guatiner', 'Guatineri', 'Guatinero', 'Guatineru', 'Guatini', 'Guatinim', 'Guatinin', 'Guatinire', 'Guatiniro', 'Guatiniru', 'Guatiniye', 'Guatino', 'Guatinom', 'Guatinon', 'Guatinore', 'Guatinoro', 'Guatinoru', 'Guatinoya', 'Guatire', 'Guatirea', 'Guatireo', 'Guatireu', 'Guatiro', 'Guatirom', 'Guatiron', 'Guatirore', 'Guatiroro', 'Guatiroru', 'Guatiroya', 'Guatiru', 'Guatirum', 'Guatirun', 'Guatirure', 'Guatiruro', 'Guatiruru', 'Guatiruya', 'Guatisa', 'Guatisai', 'Guatisao', 'Guatisau', 'Guatise', 'Guatiseo', 'Guatiseu', 'Guatisi', 'Guatisim', 'Guatisio', 'Guatisir', 'Guatisire', 'Guatisiro', 'Guatisiru', 'Guatisiya', 'Guatiso', 'Guatisom', 'Guatison', 'Guatisore', 'Guatisoro', 'Guatisoru', 'Guatisoya', 'Guatisu', 'Guatisum', 'Guatisun', 'Guatisure', 'Guatisuro', 'Guatisuru', 'Guatisuya', 'Guatita', 'Guatitai', 'Guatitao', 'Guatitau', 'Guatite', 'Guatiteo', 'Guatiteu', 'Guatiti', 'Guatitim', 'Guatitio', 'Guatitir', 'Guatitire', 'Guatitiro', 'Guatitiru', 'Guatitiya', 'Guatito', 'Guatitom', 'Guatiton', 'Guatitore', 'Guatitoro', 'Guatitoru', 'Guatitoya', 'Guatitu', 'Guatitum', 'Guatitun', 'Guatiture', 'Guatituro', 'Guatituru', 'Guatituya', 'Guatiya', 'Guatiyai', 'Guatiyao', 'Guatiyau', 'Guatiye', 'Guatiyeo', 'Guatiyeu', 'Guatiyi', 'Guatiyim', 'Guatiyio', 'Guatiyir', 'Guatiyire', 'Guatiyiro', 'Guatiyiru', 'Guatiyiya', 'Guatiyo', 'Guatiyom', 'Guatiyon', 'Guatiyore', 'Guatiyoro', 'Guatiyoru', 'Guatiyoya', 'Guatiyu', 'Guatiyum', 'Guatiyun', 'Guatiyure', 'Guatiyuro', 'Guatiyuru', 'Guatiyuya'],
  'Katsina': ['Achalonu', 'Achinacho', 'Achimshi', 'Achimtumi', 'Achimzi', 'Adiaha', 'Ajia', 'Ajiga', 'Akpakwo', 'Akpeze', 'Akpuma', 'Akpume', 'Akuka', 'Akukwa', 'Akwa', 'Akwaeze', 'Akwaeze', 'Akwafo', 'Akwaishe', 'Akwaizo', 'Akwaluma', 'Akwalume', 'Akwamili', 'Akwamkpo', 'Akwamowo', 'Akwan', 'Akwana', 'Akwanaw', 'Akwane', 'Akwanije', 'Akwanke', 'Akwanleze', 'Akwannachi', 'Akwannam', 'Akwanne', 'Akwanoko', 'Akwanue', 'Akwanukwu', 'Akwanwa', 'Akwanwo', 'Akwao', 'Akwaphi', 'Akwaraogu', 'Akwarazo', 'Akware', 'Akwaria', 'Akwarie', 'Akwarife', 'Akwarika', 'Akwarika', 'Akwarike', 'Akwarike', 'Akwariko', 'Akwarilo', 'Akwarimo', 'Akwarine', 'Akwarini', 'Akwarina', 'Akwaro', 'Akwarok', 'Akwarom', 'Akwaron', 'Akwaro', 'Akwaropo', 'Akwaror', 'Akwarosika', 'Akwarra', 'Akwarrama', 'Akwarrara', 'Akwarre', 'Akwarria', 'Akwarrih', 'Akwarrika', 'Akwarrike', 'Akwarrikoh', 'Akwarrilim', 'Akwarrim', 'Akwarro', 'Akwarra', 'Akwarray', 'Akwarre', 'Akwarreh', 'Akwarri', 'Akwarrim', 'Akwarrini', 'Akwarrino', 'Akwarrip', 'Akwarripa', 'Akwarripeme', 'Akwarripo', 'Akwarrisha', 'Akwarrisim', 'Akwarriso', 'Akwarrisu', 'Akwarriya', 'Akwarriyi', 'Akwarriyo', 'Akwarriyu', 'Akwarrizim', 'Akwaro', 'Akwaroa', 'Akwarobe', 'Akwarobo', 'Akwarobu', 'Akwarodo', 'Akwarofu', 'Akwarogo', 'Akwaroha', 'Akwaroha', 'Akwarohia', 'Akwarohim', 'Akwarohina', 'Akwarohinkpo', 'Akwarohio', 'Akwarohire', 'Akwarohisim', 'Akwarohisi', 'Akwarohiso', 'Akwarohisu', 'Akwarohiya', 'Akwarohiyi', 'Akwarohiyo', 'Akwarohiyu', 'Akwarohu', 'Akwarohume', 'Akwarohumeme', 'Akwarohumo', 'Akwarohun', 'Akwarohunga', 'Akwarohunge', 'Akwarohunga', 'Akwarohuo', 'Akwarohurahi', 'Akwarohurama', 'Akwarohuri', 'Akwarohurim', 'Akwarohuro', 'Akwarohurokpo', 'Akwarohurukpokpo', 'Akwarohusim', 'Akwarohusi', 'Akwarohuso', 'Akwarohusu', 'Akwarohuya', 'Akwarohuyaeme', 'Akwarohuyamba', 'Akwarohuye', 'Akwarohuyi', 'Akwarohuyo', 'Akwarohuyu', 'Akwaro', 'Akwaroi', 'Akwaroika', 'Akwaroike', 'Akwaroike', 'Akwaroiko', 'Akwaroila', 'Akwaroilim', 'Akwaroilo', 'Akwaroilu', 'Akwaroima', 'Akwaroimim', 'Akwaroimo', 'Akwaroimu', 'Akwaroin', 'Akwaroina', 'Akwaroine', 'Akwaroini', 'Akwaroino', 'Akwaroinu', 'Akwaroinya', 'Akwarok', 'Akwaroka', 'Akwaroki', 'Akwaroko', 'Akwaroku', 'Akwarokwa', 'Akwarokwe', 'Akwarokwi', 'Akwarokwo', 'Akwarokwu', 'Akwaroma', 'Akwaromaji', 'Akwaroman', 'Akwaromana', 'Akwaromani', 'Akwaromano', 'Akwaromanu', 'Akwaromanya', 'Akwaromasi', 'Akwaromasu', 'Akwaromasya', 'Akwaromatari', 'Akwaromaya', 'Akwaromayaeme', 'Akwarombale', 'Akwarombo', 'Akwarombu', 'Akwaromi', 'Akwaromidi', 'Akwaromidia', 'Akwaromidie', 'Akwaromidim', 'Akwaromidin', 'Akwaromidini', 'Akwaromidino', 'Akwaromidinu', 'Akwaromidia', 'Akwaromidi', 'Akwaromidir', 'Akwaromidreh', 'Akwaromidreim', 'Akwaromidri', 'Akwaromidro', 'Akwaromidru', 'Akwaromidrya', 'Akwaromidryi', 'Akwaromidryo', 'Akwaromidryu', 'Akwaromidsia', 'Akwaromidsie', 'Akwaromsim', 'Akwaromidso', 'Akwaromidsu', 'Akwaromidsya', 'Akwaromidui', 'Akwaromiduo', 'Akwaromiduu', 'Akwaromidya', 'Akwaromidyai', 'Akwaromidyaie', 'Akwaromidyaim', 'Akwaromidyan', 'Akwaromidyani', 'Akwaromidyano', 'Akwaromidyanu', 'Akwaromidyanya', 'Akwaromidye', 'Akwaromidyei', 'Akwaromidyem', 'Akwaromidyen', 'Akwaromidyeni', 'Akwaromidyeno', 'Akwaromidyenu', 'Akwaromidyenya', 'Akwaromidyi', 'Akwaromidyie', 'Akwaromidyim', 'Akwaromidyin', 'Akwaromidyini', 'Akwaromidyino', 'Akwaromidyinu', 'Akwaromidyinha', 'Akwaromidyo', 'Akwaromidyoi', 'Akwaromidyom', 'Akwaromidyon', 'Akwaromidyoni', 'Akwaromidyono', 'Akwaromidyonu', 'Akwaromidyonya', 'Akwaromidyu', 'Akwaromidyui', 'Akwaromidyum', 'Akwaromidyun', 'Akwaromidyuni', 'Akwaromidyuno', 'Akwaromidyunu', 'Akwaromidyunya', 'Akwaromo', 'Akwaromi', 'Akwaromii', 'Akwaromio', 'Akwaromiu', 'Akwaromiame', 'Akwaromiama', 'Akwaromiane', 'Akwaromiani', 'Akwaromiania', 'Akwaromianiame', 'Akwaromianie', 'Akwaromiania', 'Akwaromiani', 'Akwaromiania', 'Akwaromiano', 'Akwaromianu', 'Akwaromianya', 'Akwaromianya', 'Akwaromibe', 'Akwaromibi', 'Akwaromibia', 'Akwaromibie', 'Akwaromibim', 'Akwaromibina', 'Akwaromibine', 'Akwaromibini', 'Akwaromibino', 'Akwaromibinu', 'Akwaromibinya', 'Akwaromibo', 'Akwaromibu', 'Akwaromibum', 'Akwaromibun', 'Akwaromibunga', 'Akwaromibunge', 'Akwaromibungi', 'Akwaromibuno', 'Akwaromibunu', 'Akwaromibuya', 'Akwaromibuyi', 'Akwaromibuyia', 'Akwaromibuyim', 'Akwaromibuyina', 'Akwaromibuyine', 'Akwaromibuyini', 'Akwaromibuyino', 'Akwaromibuyinu', 'Akwaromibuyo', 'Akwaromibuypa', 'Akwaromibuyque', 'Akwaromibuyp', 'Akwaromibuyqu', 'Akwaromibuyya', 'Akwaromibuyyi', 'Akwaromibuyyo', 'Akwaromibuyyu', 'Akwaromice', 'Akwaromicea', 'Akwaromicee', 'Akwaromicei', 'Akwaromicem', 'Akwaromicena', 'Akwaromicharge', 'Akwaromiches', 'Akwaromichess', 'Akwaromichest', 'Akwaromicho', 'Akwaromichs', 'Akwaromichwa', 'Akwaromichwe', 'Akwaromichewi', 'Akwaromichews', 'Akwaromichey', 'Akwaromicheys', 'Akwaromichz', 'Akwaromici', 'Akwaromicias', 'Akwaromicide', 'Akwaromicided', 'Akwaromicidee', 'Akwaromicidel', 'Akwaromiciden', 'Akwaromicides', 'Akwaromicidess', 'Akwaromicidest', 'Akwaromicidia', 'Akwaromicidial', 'Akwaromicidian', 'Akwaromicidians', 'Akwaromicie', 'Akwaromicies', 'Akwaromiciferous', 'Akwaromicillin', 'Akwaromicin', 'Akwaromicins', 'Akwaromicio', 'Akwaromicios', 'Akwaromicius', 'Akwaromico', 'Akwaromicoal', 'Akwaromicode', 'Akwaromicod', 'Akwaromicode', 'Akwaromicodes', 'Akwaromicola', 'Akwaromicor', 'Akwaromicos', 'Akwaromicot', 'Akwaromicota', 'Akwaromicota', 'Akwaromicote', 'Akwaromicotes', 'Akwaromicotina', 'Akwaromicro', 'Akwaro', 'Akwaroma', 'Akwaromi', 'Akwaromim', 'Akwaromo', 'Akwaromu'],
  'Kebbi': ['Aleiro', 'Arewa', 'Argungu', 'Augie', 'Bagudo', 'Birnin Kebbi', 'Bunza', 'Dandi', 'Farinkon', 'Gwadabawa', 'Jega', 'Jibiya', 'Kabba', 'Kalankasuwa', 'Karaye', 'Katsina', 'Kiyawa', 'Kolere', 'Kulle', 'Kware', 'Maiyama', 'Makera', 'Marnona', 'Sakaba', 'Sanda', 'Shanga', 'Suru', 'Unguwar Rami', 'Wasagu', 'Yauri'],
  'Kogi': ['Adavi', 'Ajaokuta', 'Ankpa', 'Bassa', 'Dekina', 'Ibaji', 'Idah', 'Igalamela-Odolu', 'Ijumu', 'Kabba/Bunnu', 'Kogue', 'Lokoja', 'Mopa-Moto', 'Ofu', 'Ogori/Magongo', 'Okehi', 'Okene', 'Olamaboro', 'Omala', 'Omi', 'Orishcev', 'Osa', 'Otenyi', 'Ovoko', 'Oyigbo', 'Turan', 'Yagba East', 'Yagba West'],
  'Kwara': ['Asa', 'Baruten', 'Edu', 'Ekiti', 'Ifelodun', 'Ilorin East', 'Ilorin South', 'Ilorin West', 'Irepodun', 'Isin', 'Kaiama', 'Moro', 'Offa', 'Oke Ero', 'Oyun', 'Pategi', 'Surulere'],
  'Lagos': ['Agege', 'Ajeromi-Ifelodun', 'Alimosho', 'Amuwo-Odofin', 'Apapa', 'Badagry', 'Bariga', 'Ekanmekun', 'Ejigbo', 'Epe', 'Eti-Osa', 'Gbomosha', 'Ifako-Ijaiye', 'Ikorodu', 'Ikoyi', 'Ikeja', 'Ikotun-Igando', 'Ilupeju', 'Isolo', 'Lagos Island', 'Lagos Mainland', 'Lekki', 'Mushin', 'Ojodu', 'Ojo', 'Okeodo', 'Oshodi-Isolo', 'Mainland', 'Yaba'],
  'Nasarawa': ['Akwanga', 'Awe', 'Doma', 'Gadabuke', 'Gudi', 'Kagarko', 'Keana', 'Keffi', 'Kokona', 'Lafia', 'Nasarawa', 'Nasarawa Egon', 'Obi', 'Toto', 'Wamba'],
  'Niger': ['Agaie', 'Agwara', 'Bida', 'Borgu', 'Bosso', 'Chanchaga', 'Edati', 'Gbako', 'Gurara', 'Katcha', 'Kontagora', 'Lapai', 'Lavun', 'Magama', 'Mariga', 'Mashegu', 'Mokwa', 'Mopamuro', 'Munya', 'Paikoro', 'Rafi', 'Rijau', 'Suleja', 'Takalakimiri', 'Tegina', 'Tengi', 'Tungan Kawo', 'Ushongo', 'Wushishi', 'Yenegoa'],
  'Ogun': ['Abeokuta North', 'Abeokuta South', 'Ado-Odo/Ota', 'Ewekoro', 'Idaraoloji', 'Ifo', 'Ijebu East', 'Ijebu North', 'Ijebu North East', 'Ijebu Ode', 'Ijegun-Isin', 'Ikenne', 'Imeko-Afon', 'Ipokia', 'Irewole', 'Isimiri', 'Itamodun', 'Iwopin', 'Obafemi-Owode', 'Odeda', 'Odogbolu', 'Ogbere', 'Ogunmakin', 'Okitipupa', 'Okodan', 'Okuta', 'Oluji/Ilesha', 'Oluyole', 'Omu Aran', 'Onibe', 'Oniko', 'Orike', 'Orile-Owu', 'Ososa', 'Oyo', 'Oyoko', 'Shagamu', 'Yewa North', 'Yewa South'],
  'Ondo': ['Akoko North-East', 'Akoko North-West', 'Akoko South-East', 'Akoko South-West', 'Akure North', 'Akure South', 'Ila', 'Ile-Oluji/Okeigbo', 'Irele', 'Irhin', 'Isan', 'Isoko', 'Iyooma', 'Okitipupa', 'Ondo East', 'Ondo West', 'Orobo', 'Ose', 'Owo'],
  'Osun': ['Atakunosa', 'Atakumosa East', 'Atakumosa West', 'Ede North', 'Ede South', 'Ede-Osu', 'Egbedore', 'Ejigbo', 'Ifelodun', 'Ifedayo', 'Ifelogbo', 'Igembe', 'Igotagbe', 'Iife', 'Ijero', 'Ijerogbo', 'Ijesa', 'Ijesa East', 'Ijesa South-East', 'Ikirun', 'Ikire', 'Ikiti', 'Iklaa', 'Iko', 'Ikola', 'Ikoyi', 'Ilaje', 'Ilaji', 'Ilara', 'Ilaramaja', 'Ilase', 'Ilase-Arue', 'Ilase-Iborapa', 'Ilase-Nla', 'Ilase-Oki', 'Ilase-Okuku', 'Ilase-Osa', 'Ilase-Otan', 'Ilase-Owu', 'Ilase-Oye', 'Ilase-Oyoko', 'Ilashe', 'Ilasi', 'Ilasi-Asha', 'Ilasi-Atan', 'Ilasi-Ayabe', 'Ilasi-Aya', 'Ilasi-Bada', 'Ilasi-Beere', 'Ilasi-Eginmi', 'Ilasi-Elegun', 'Ilasi-Elo', 'Ilasi-Eloro', 'Ilasi-Elore', 'Ilasi-Fajobi', 'Ilasi-Funke', 'Ilasi-Gara', 'Ilasi-Gbaga', 'Ilasi-Gbagura', 'Ilasi-Gbana', 'Ilasi-Gbaramatu', 'Ilasi-Gbari', 'Ilasi-Gbata', 'Ilasi-Gbati', 'Ilasi-Gbayan', 'Ilasi-Gbera', 'Ilasi-Gberan', 'Ilasi-Gberana', 'Ilasi-Gbere', 'Ilasi-Gberiga', 'Ilasi-Gbeso', 'Ilasi-Gbeta', 'Ilasi-Gbeyin', 'Ilasi-Gbeyo', 'Ilasi-Gbeyu', 'Ilasi-Gebeyin', 'Ilasi-Geda', 'Ilasi-Gedado', 'Ilasi-Gede', 'Ilasi-Gedei', 'Ilasi-Gedele', 'Ilasi-Geden', 'Ilasi-Gedena', 'Ilasi-Gedenle', 'Ilasi-Gedeo', 'Ilasi-Gederna', 'Ilasi-Gedetun', 'Ilasi-Gedevatu', 'Ilasi-Gedeyin', 'Ilasi-Gedeyina', 'Ilasi-Gedinla', 'Ilasi-Gedo', 'Ilasi-Gedube', 'Ilasi-Gedule', 'Ilasi-Gedu', 'Ilasi-Gedumle', 'Ilasi-Gedun', 'Ilasi-Geduna', 'Ilasi-Gedunla', 'Ilasi-Gedunle', 'Ilasi-Geduno', 'Ilasi-Gedunun', 'Ilasi-Gedunya', 'Ilasi-Gedunya', 'Ilasi-Geduya', 'Ilasi-Geduyanda', 'Ilasi-Geduyan', 'Ilasi-Geduz', 'Ilasi-Gedwa', 'Ilasi-Gedwei', 'Ilasi-Gedwele', 'Ilasi-Gedwen', 'Ilasi-Gedwena', 'Ilasi-Gedweni', 'Ilasi-Gedweno', 'Ilasi-Gedwenu', 'Ilasi-Gedwenya', 'Ilasi-Gedwey', 'Ilasi-Gedweya', 'Ilasi-Gedwey', 'Ilasi-Gedwezi', 'Ilasi-Gedwi', 'Ilasi-Gedwi', 'Ilasi-Gedwia', 'Ilasi-Gedwie', 'Ilasi-Gedwin', 'Ilasi-Gedwina', 'Ilasi-Gedwine', 'Ilasi-Gedwini', 'Ilasi-Gedwino', 'Ilasi-Gedwinu', 'Ilasi-Gedwinya', 'Ilasi-Gedwo', 'Ilasi-Gedwon', 'Ilasi-Gedwona', 'Ilasi-Gedwone', 'Ilasi-Gedwoni', 'Ilasi-Gedwono', 'Ilasi-Gedwonu', 'Ilasi-Gedwonya', 'Ilasi-Gedwu', 'Ilasi-Gedwu', 'Ilasi-Gedwua', 'Ilasi-Gedwue', 'Ilasi-Gedwun', 'Ilasi-Gedwuna', 'Ilasi-Gedwune', 'Ilasi-Gedwuni', 'Ilasi-Gedwuno', 'Ilasi-Gedwunu', 'Ilasi-Gedwunya', 'Ilasi-Gedwuy', 'Ilasi-Gedwuya', 'Ilasi-Gedwuy', 'Ilasi-Gedwuzi', 'Ilasi-Gedy', 'Ilasi-Gedya', 'Ilasi-Gedyai', 'Ilasi-Gedyain', 'Ilasi-Gedyaina', 'Ilasi-Gedyaine', 'Ilasi-Gedyaini', 'Ilasi-Gedyaino', 'Ilasi-Gedyainu', 'Ilasi-Gedyainya', 'Ilasi-Gedyaj', 'Ilasi-Gedyane', 'Ilasi-Gedyanei', 'Ilasi-Gedyanele', 'Ilasi-Gedyanen', 'Ilasi-Gedyanena', 'Ilasi-Gedyaneni', 'Ilasi-Gedyaneno', 'Ilasi-Gedyanenu', 'Ilasi-Gedyanenya', 'Ilasi-Gedyaney', 'Ilasi-Gedyaneya', 'Ilasi-Gedyane', 'Ilasi-Gedyanezi', 'Ilasi-Gedyani', 'Ilasi-Gedyani', 'Ilasi-Gedyania', 'Ilasi-Gedyanie', 'Ilasi-Gedyanim', 'Ilasi-Gedyanina', 'Ilasi-Gedyanine', 'Ilasi-Gedyanini', 'Ilasi-Gedyanino', 'Ilasi-Gedyaninu', 'Ilasi-Gedyaninya', 'Ilasi-Gedyaniya', 'Ilasi-Gedyano', 'Ilasi-Gedyanoi', 'Ilasi-Gedyanoia', 'Ilasi-Gedyanoie', 'Ilasi-Gedyanoin', 'Ilasi-Gedyanoina', 'Ilasi-Gedyanoine', 'Ilasi-Gedyanoini', 'Ilasi-Gedyanoino', 'Ilasi-Gedyanoinu', 'Ilasi-Gedyanoinya', 'Ilasi-Gedyanoj', 'Ilasi-Gedyanone', 'Ilasi-Gedyanonel', 'Ilasi-Gedyanonen', 'Ilasi-Gedyanonena', 'Ilasi-Gedyanoneni', 'Ilasi-Gedyanoneno', 'Ilasi-Gedyononenu', 'Ilasi-Gedyononenya', 'Ilasi-Gedyanonej', 'Ilasi-Gedyanoone', 'Ilasi-Gedyoneel', 'Ilasi-Gedynoneen', 'Ilasi-Gedynonena', 'Ilasi-Gedyoneni', 'Ilasi-Gedyoneno', 'Ilasi-Gedyonenum', 'Ilasi-Gedyoneny', 'Ilasi-Gedyonoey', 'Ilasi-Gedyonoeya', 'Ilasi-Gedyonoey', 'Ilasi-Gedyonoezi', 'Ilasi-Gedyoni', 'Ilasi-Gedyonii', 'Ilasi-Gedyonia', 'Ilasi-Gedyonie', 'Ilasi-Gedyonim', 'Ilasi-Gedyonina', 'Ilasi-Gedyonine', 'Ilasi-Gedyonini', 'Ilasi-Gedyonino', 'Ilasi-Gedyoninu', 'Ilasi-Gedyoninya', 'Ilasi-Gedyoniya', 'Ilasi-Gedyonoa', 'Ilasi-Gedyonoai', 'Ilasi-Gedyonoaia', 'Ilasi-Gedyonoaie', 'Ilasi-Gedyonoain', 'Ilasi-Gedyonoaina', 'Ilasi-Gedyonoaine', 'Ilasi-Gedyonoaini', 'Ilasi-Gedyonoaino', 'Ilasi-Gedyonoainu', 'Ilasi-Gedyonoainya', 'Ilasi-Gedyonoaj', 'Ilasi-Gedyonoae', 'Ilasi-Gedyonoael', 'Ilasi-Gedyonoaen', 'Ilasi-Gedyonoaena', 'Ilasi-Gedyonoaeni', 'Ilasi-Gedyonoaeno', 'Ilasi-Gedyonoaenu', 'Ilasi-Gedyonoaenya', 'Ilasi-Gedyonoaey', 'Ilasi-Gedyonoaeya', 'Ilasi-Gedyonoaey', 'Ilasi-Gedyonoaezi', 'Ilasi-Gedyonoa', 'Ilasi-Gedyonoe', 'Ilasi-Gedyonoei', 'Ilasi-Gedyonoeia', 'Ilasi-Gedyonoeie', 'Ilasi-Gedyonoein', 'Ilasi-Gedyonoeina', 'Ilasi-Gedyonoeine', 'Ilasi-Gedyonoeini', 'Ilasi-Gedyonoeino', 'Ilasi-Gedyonoinu', 'Ilasi-Gedyonoeiny', 'Ilasi-Gedyonoej', 'Ilasi-Gedyonoele', 'Ilasi-Gedyonoelei', 'Ilasi-Gedyonioelia', 'Ilasi-Gedyonoelihe', 'Ilasi-Gedyonoelin', 'Ilasi-Gedyonoelina', 'Ilasi-Gedyonoeline', 'Ilasi-Gedyonolini', 'Ilasi-Gedyonolino', 'Ilasi-Gedyonoline', 'Ilasi-Gedyonolicy', 'Ilasi-Gedyonolenvia', 'Ilasi-Gedyonorency', 'Ilasi-Gedyononey', 'Ilasi-Gedyononeya', 'Ilasi-Gedyonoey', 'Ilasi-Gedyonoeezi', 'Ilasi-Gedyonoia', 'Ilasi-Gedyonoii', 'Ilasi-Gedyonoiia', 'Ilasi-Gedyonoiie', 'Ilasi-Gedyonoiin', 'Ilasi-Gedyonoiina', 'Ilasi-Gedyonoiine', 'Ilasi-Gedyonoiini', 'Ilasi-Gedyonoiino', 'Ilasi-Gedyonoiinu', 'Ilasi-Gedyonoiinya', 'Ilasi-Gedyonoij', 'Ilasi-Gedyonoiy', 'Ilasi-Gedyonoiye', 'Ilasi-Gedyonoiyei', 'Ilasi-Gedyonoiyele', 'Ilasi-Gedyonoiyen', 'Ilasi-Gedyonoiyena', 'Ilasi-Gedyonoiyeni', 'Ilasi-Gedyonoiyeno', 'Ilasi-Gedyonoiyenu', 'Ilasi-Gedyonoiyenya', 'Ilasi-Gedyonoiyey', 'Ilasi-Gedyonoiyeya', 'Ilasi-Gedyonoiyey', 'Ilasi-Gedyonoiyezi', 'Ilasi-Gedyonoi', 'Ilasi-Gedyonoio', 'Ilasi-Gedyonoioa', 'Ilasi-Gedyonoioai', 'Ilasi-Gedyonioaia', 'Ilasi-Gedyonoioaie', 'Ilasi-Gedyonoioain', 'Ilasi-Gedyonioaina', 'Ilasi-Gedyonoioaine', 'Ilasi-Gedyonioaini', 'Ilasi-Gedyonioaino', 'Ilasi-Gedyonoioainu', 'Ilasi-Gedyonoioainya', 'Ilasi-Gedyonoioaj', 'Ilasi-Gedyonioae', 'Ilasi-Gedyonoioael', 'Ilasi-Gedyonoioaen', 'Ilasi-Gedyonoioaena', 'Ilasi-Gedyonoioaeni', 'Ilasi-Gedyonoioaeno', 'Ilasi-Gedyonoioaenu', 'Ilasi-Gedyonoioaenya', 'Ilasi-Gedyonoioaey', 'Ilasi-Gedyonoioaeya', 'Ilasi-Gedyonoioaey', 'Ilasi-Gedyonoioaezi', 'Ilasi-Gedyonoioai', 'Ilasi-Gedyonoioau', 'Ilasi-Gedyonoioaua', 'Ilasi-Gedyonoioaue', 'Ilasi-Gedyonoioaun', 'Ilasi-Gedyonoioauna', 'Ilasi-Gedyonoioaune', 'Ilasi-Gedyonoioauni', 'Ilasi-Gedyonoioauno', 'Ilasi-Gedyonoioaunu', 'Ilasi-Gedyonoioauya'],
  'Oyo': ['Addo-Odo/Ota', 'Abeokuta North', 'Abeokuta South', 'Abeokuta South-East', 'Abeokuta West', 'Ajodan', 'Aladelumoye', 'Alador', 'Alafara', 'Alagbe', 'Alagbon', 'Alagbundu', 'Alago', 'Alagumeji', 'Alagunya', 'Alaguoji', 'Alahun', 'Alajara', 'Alaje', 'Alajoba', 'Alajoda', 'Alajola', 'Alajoya', 'Alalaro', 'Alali', 'Alamo', 'Alamulati', 'Alamu', 'Alanuwa', 'Alanzi', 'Alanziejo', 'Alao', 'Alaodun', 'Alaofe', 'Alaokan', 'Alaoke', 'Alaokin', 'Alaokunle', 'Alaolako', 'Alaolanre', 'Alaolapo', 'Alaolapote', 'Alaolapoti', 'Alaolata', 'Alaolataro', 'Alaole', 'Alaolenege', 'Alaolenia', 'Alaoleo', 'Alaoletu', 'Alaolewa', 'Alaoley', 'Alaolez', 'Alaoli', 'Alaolin', 'Alaoling', 'Alaolinke', 'Alaolinko', 'Alaolioyu', 'Alaolipu', 'Alaolike', 'Alaolikpo', 'Alaolikpu', 'Alaoli', 'Alaolon', 'Alaolong', 'Alaolongo', 'Alaolonju', 'Alaolonje', 'Alaolong', 'Alaolonje', 'Alaolon', 'Alaolong', 'Alaolon', 'Alaolon', 'Alaolon', 'Alaolon', 'Alaolon', 'Alaolon', 'Alaolon', 'Alaolon', 'Alaolon', 'Alaolon'],
  'Plateau': ['Barkin Ladi', 'Barikin Ladi', 'Bassa', 'Bokkos', 'Mangu', 'Mikang', 'Pankshin', 'Quan Pan', 'Riyom', 'Shendam', 'Wase'],
  'Rivers': ['Abua/Odual', 'Akuku-Toru', 'Andoni', 'Asari-Toru', 'Bonny', 'Buguma', 'Degema', 'Eleme', 'Emuoha', 'Etche', 'Gokana', 'Gokere', 'Gokere', 'Gokro', 'Gokro', 'Gokro', 'Gokro', 'Gokro', 'Gokro', 'Gokro', 'Gokro', 'Gokro', 'Gomoale', 'Gomofere', 'Gomoreme', 'Gomoware', 'Gomu', 'Gonacul', 'Gonahaem', 'Gonahaemfua', 'Gonale', 'Gonalebi', 'Gonalema', 'Gonalepa', 'Gonalepeka', 'Gonalepe', 'Gonalepefua', 'Gonalepu', 'Gonaletaa', 'Gonaletaba', 'Gonaletafe', 'Gonaletai', 'Gonaletaka', 'Gonaletama', 'Gonaletapa', 'Gonaletape', 'Gonaletara', 'Gonaletase', 'Gonaletate', 'Gonaletava', 'Gonaletawa', 'Gonaletaya', 'Gonaleteka', 'Gonaletema', 'Gonaletena', 'Gonaletesa', 'Gonaletewa', 'Gonaletoba', 'Gonaletoca', 'Gonaletoda', 'Gonaletoe', 'Gonaletofs', 'Gonaletoga', 'Gonaletoia', 'Gonaletoko', 'Gonaletokpa', 'Gonaletola', 'Gonaletoma', 'Gonaletona', 'Gonaletoo', 'Gonaletopa', 'Gonaletora', 'Gonaletosa', 'Gonaletota', 'Gonaletouvia', 'Gonaletowa', 'Gonaletoya', 'Gonaletoza', 'Gonaletubia', 'Gonaletue', 'Gonaletu', 'Gonaletua', 'Gonaletuba', 'Gonaletuca', 'Gonaletuda', 'Gonaletue', 'Gonaletuf', 'Gonaletuga', 'Gonaletuh', 'Gonaletui', 'Gonaletuj', 'Gonaletuka', 'Gonaletul', 'Gonaletula', 'Gonaletum', 'Gonaletuna', 'Gonaletuno', 'Gonaletuo', 'Gonaletupa', 'Gonaletuppa', 'Gonaletupra', 'Gonatetu', 'Gonatetua', 'Gonatetua', 'Gonatetub', 'Gonatetuc', 'Gonatetud', 'Gonatetue', 'Gonatetuf', 'Gonatetug', 'Gonatetuh', 'Gonatetui', 'Gonatetuj', 'Gonatetuk', 'Gonatetul', 'Gonatetuma', 'Gonatetun', 'Gonatetuo', 'Gonatetup', 'Gonatetuppa', 'Gonatetupra', 'Gonatetur', 'Gonatetus', 'Gonatetut', 'Gonatetuu', 'Gonatetuv', 'Gonatetuw', 'Gonatetux', 'Gonatetuy', 'Gonatetuz'],
  'Sokoto': ['Binji', 'Bodinga', 'Dange-Shuni', 'Gada', 'Gawabawa', 'Goronyo', 'Gudu', 'Gummi', 'Gwadabawa', 'Illela', 'Isa', 'Kware', 'Rabah', 'Sabon-Birni', 'Sanyinsinma', 'Sapade', 'Shagari', 'Shuni', 'Sokoto North', 'Sokoto South', 'Tambuwal', 'Tangaza', 'Tureta', 'Wamakko', 'Wamako', 'Yaaba', 'Yabo', 'Yagba', 'Yakoba', 'Yanoto', 'Yarda', 'Yarima'],
  'Taraba': ['Ardo-Kola', 'Bali', 'Balanga', 'Bambur', 'Banja', 'Bantaje', 'Bwambum', 'Donga', 'Gajere', 'Gandu', 'Gashaka', 'Gassol', 'Gida', 'Gidawaje', 'Gigaje', 'Gikere', 'Girke', 'Girube', 'Giza', 'Gizaje', 'Gizaje', 'Gizaje', 'Gizaje', 'Gizaje', 'Gizaje', 'Gizaje', 'Gizaje', 'Gizaje', 'Gizaje', 'Gizaje', 'Gizaje', 'Gizaje', 'Gizaje', 'Gizaje', 'Gizaje', 'Gizaje', 'Gizaje', 'Gizaje'],
  'Yobe': ['Bade', 'Bursari', 'Damaturu', 'Geidam', 'Gujba', 'Gulani', 'Jakusko', 'Kakasuwa', 'Karawa', 'Kasuwa', 'Kasuwa Saida', 'Katsina', 'Kauri', 'Kauyelo', 'Kawaje', 'Kawaje', 'Kawaje', 'Kawaje', 'Kawaje', 'Kawaje', 'Kawaje', 'Kawaje', 'Kawaje', 'Kawaje', 'Kawaje', 'Kawaje', 'Kawaje', 'Kawaje', 'Kawaje', 'Kawaje', 'Kawaje', 'Kawaje', 'Kawaje'],
  'Zamfara': ['Anka', 'Bakura', 'Birnin Magaji', 'Birnin Magaji/Kasuwa', 'Bungudu', 'Gada', 'Giadawa', 'Gummi', 'Gusau', 'Kaura Namoda', 'Kawale', 'Magami', 'Mada', 'Maradun', 'Markofu', 'Markofu', 'Markofu', 'Markofu', 'Markofu', 'Markofu', 'Markofu', 'Markofu', 'Markofu'],
};

const normalizeStateValue = (value) => {
  if (!value) return "";
  const trimmed = String(value).trim();
  if (!trimmed) return "";
  const byCode = stateNameByCode[trimmed.toUpperCase()];
  if (byCode) return byCode;
  return trimmed;
};

const getSafeLgas = (stateName) => {
  try {
    const normalizedStateName = normalizeStateValue(stateName);
    if (!normalizedStateName) return [];
    const mappedLgas = lgaMapping[normalizedStateName];
    if (mappedLgas && Array.isArray(mappedLgas)) {
      return mappedLgas;
    }
    return [];
  } catch (_) {
    return [];
  }
};

const STORAGE_KEY = "bcs_register_draft";
const saveDraft = (data) => { try { localStorage.setItem(STORAGE_KEY, JSON.stringify(data)); } catch (_) { } };
const loadDraft = () => { try { const d = localStorage.getItem(STORAGE_KEY); return d ? JSON.parse(d) : {}; } catch (_) { return {}; } };
const clearDraft = () => { try { localStorage.removeItem(STORAGE_KEY); } catch (_) { } };

const Field = ({ name, label, required = true, children, span = 1 }) => (
  <div className={span === 2 ? "col-span-1 sm:col-span-2" : "col-span-1"}>
    <label htmlFor={name} className="text-[10px] font-bold tracking-widest uppercase text-gray-400 block mb-2">{label}</label>
    {children}
    {required && <div className="text-[10px] text-gray-400 mt-1">Required</div>}
  </div>
);

const inputCls = "h-11 rounded-none border-0 border-b border-gray-300 bg-transparent px-0 text-sm font-medium text-gray-800 focus:ring-0 hover:border-blue-400 focus:border-blue-600";
const selectCls = "h-11 w-full rounded-none border border-gray-300 bg-transparent px-3 text-sm text-gray-800 focus:outline-none focus:border-blue-600";

const STEPS = [
  { key: "account", label: "Account", desc: "Login credentials & photo" },
  { key: "personal", label: "Personal", desc: "Your name & contact info" },
  { key: "location", label: "Location", desc: "Origin & residence details" },
  { key: "bethel", label: "Bethel", desc: "Church & worship details" },
  { key: "priesthood", label: "Priesthood", desc: "Priest & office details" },
];

const Register = ({ value }) => {
  const [formData, setFormData] = useState({});
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isStakeholder, setIsStakeholder] = useState(false);
  const [isPriest, setIsPriest] = useState(false);
  const [isMember, setIsMember] = useState(true);
  const [isPosted, setIsPosted] = useState(null);
  const [qualifications, setQualifications] = useState(null);
  const [categories, setCategories] = useState([]);
  const [, setDepartments] = useState([]);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [duplicate, setDuplicate] = useState(null);
  const [verifyOpen, setVerifyOpen] = useState(false);
  // camera | scanning | checking | warning | creating | duplicate | error
  const [verifyStage, setVerifyStage] = useState("camera");
  const [verifyMessage, setVerifyMessage] = useState("");
  const [verifyCameraFailed, setVerifyCameraFailed] = useState(false);
  const navigate = useNavigate();
  const webcamRef = useRef(null);
  const verifyWebcamRef = useRef(null);
  const pendingDescriptorRef = useRef(null);
  const passportInputRef = useRef(null);
  const submittingRef = useRef(false);

  useEffect(() => {
    preloadFaceModels();
  }, []);

  // ── Restore draft on mount ──
  useEffect(() => {
    const draft = loadDraft();
    if (Object.keys(draft).length > 0) {
      const formFields = { ...draft };
      formFields.state_of_origin = normalizeStateValue(formFields.state_of_origin);
      formFields.residing_state = normalizeStateValue(formFields.residing_state);
      delete formFields._imagePreview;
      delete formFields._step;
      delete formFields._isPriest;
      delete formFields._isStakeholder;
      delete formFields._isMember;
      delete formFields._isPosted;
      delete formFields._qualifications;
      setFormData(formFields);
      if (draft._imagePreview) setImagePreview(draft._imagePreview);
      if (draft._step !== undefined) setStep(draft._step);
      if (draft._isPriest !== undefined) setIsPriest(draft._isPriest);
      if (draft._isStakeholder !== undefined) setIsStakeholder(draft._isStakeholder);
      if (draft._isMember !== undefined) setIsMember(draft._isMember);
      if (draft._isPosted !== undefined) setIsPosted(draft._isPosted);
      if (draft._qualifications !== undefined) setQualifications(draft._qualifications);
    }
  }, []);

  // ── Save draft on field change ──
  useEffect(() => {
    saveDraft({
      ...formData,
      _imagePreview: imagePreview,
      _step: step,
      _isPriest: isPriest,
      _isStakeholder: isStakeholder,
      _isMember: isMember,
      _isPosted: isPosted,
      _qualifications: qualifications,
    });
  }, [formData, imagePreview, step, isPriest, isStakeholder, isMember, isPosted, qualifications]);

  // ── Fetch categories ──
  useEffect(() => {
    const fetch2 = async () => {
      try {
        const snap = await getDocs(collection(db, "BCS"));
        const cats = new Set(), depts = new Set();
        snap.forEach((d) => {
          if (d.data().category) cats.add(d.data().category);
          if (d.data().department_name) depts.add(d.data().department_name);
        });
        setCategories(Array.from(cats));
        setDepartments(Array.from(depts));
      } catch (_) { }
    };
    fetch2();
  }, []);

  const updateField = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (["email", "phone_number", "first_name", "last_name"].includes(name)) setDuplicate(null);
  };

  const handleDocumentUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const ok = file.type.startsWith("image/") && file.size / 1024 / 1024 < 2;
    if (!ok) {
      toast.error("Image must be <2MB");
      setUploadedFile(null);
      return;
    }
    setUploadedFile(file);
    updateField("document_image", file.name);
  };

  const validateEmail = (value) => /^\S+@\S+\.\S+$/.test(value);
  const fieldLabels = {
    email: "Email address",
    password: "Password",
    first_name: "First name",
    last_name: "Last name",
    phone_number: "Phone number",
    next_of_kin1: "Next of kin",
    gender: "Gender",
    pew: "Pew",
    country_of_residence: "Country of residence",
    country_of_origin: "Country of origin",
    residing_state: "State of residence",
    residential_address: "Residential address",
    state_of_origin: "State of origin",
    lga_of_origin: "LGA / Province / County",
    village: "Village / Hometown",
    nation_of_worship: "Nation of worship",
    bethel: "Bethel name",
    bethel_Location: "Bethel address",
    bethel_country: "Bethel country",
  };

  const stepFields = {
    account: ["email", "password"],
    personal: ["first_name", "last_name", "phone_number", "next_of_kin1", "gender", "pew"],
    location: ["country_of_residence", "country_of_origin", "residing_state", "residential_address", "state_of_origin", "lga_of_origin", "village"],
    bethel: isMember ? ["nation_of_worship", "bethel", "bethel_Location", "bethel_country"] : [],
    priesthood: [],
  };

  const validateStepFields = (fields) => {
    for (const field of fields) {
      const value = formData[field];
      if (!value || String(value).trim() === "") {
        toast.error(`${fieldLabels[field] || field} is required.`);
        return false;
      }
      if (field === "email" && !validateEmail(value)) {
        toast.error("Please enter a valid email address.");
        return false;
      }
      if (field === "password" && String(value).length < 6) {
        toast.error("Password must be at least 6 characters.");
        return false;
      }
    }
    return true;
  };

  // The photo stays on the device until the account exists; Storage only accepts signed-in uploads.
  const processPhoto = async (rawDataUrl) => {
    setPhotoBusy(true);
    try {
      setImagePreview(await compressPhoto(rawDataUrl));
    } catch (err) {
      console.error("Passport photo processing failed:", err);
      toast.error("Could not use that photo. Please try another one.");
    } finally {
      setPhotoBusy(false);
    }
  };

  const capturePhoto = async () => {
    const imageSrc = webcamRef.current?.getScreenshot();
    setCameraOpen(false);
    if (!imageSrc) {
      toast.error("Could not capture from the camera. You can upload a photo instead.");
      return;
    }
    await processPhoto(imageSrc);
  };

  const handlePassportFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file (JPG or PNG).");
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      toast.error("That image is too large. Please choose one under 15MB.");
      return;
    }
    try {
      await processPhoto(await readFileAsDataUrl(file));
    } catch (_) {
      toast.error("Could not read that image. Please try another photo.");
    }
  };

  const nextStep = () => {
    if (step === 0 && !imagePreview) {
      toast.error("Please take or upload your passport photograph before continuing.");
      return;
    }
    if (!validateStepFields(stepFields[STEPS[step].key])) return;
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
    window.scrollTo(0, 0);
  };

  const prevStep = () => { setStep((s) => Math.max(s - 1, 0)); window.scrollTo(0, 0); };

  /**
   * Creates the login, or recovers one left behind by an earlier attempt that failed
   * mid-way (e.g. network drop after the account was created but before the profile saved).
   */
  const createOrRecoverAccount = async (email, password) => {
    try {
      return (await createUserWithEmailAndPassword(auth, email, password)).user;
    } catch (error) {
      if (error.code !== "auth/email-already-in-use") throw error;
      let existing;
      try {
        existing = (await signInWithEmailAndPassword(auth, email, password)).user;
      } catch (_) {
        showDuplicate({
          field: "email",
          message: 'This email address is already registered. Please log in instead — use "Forgot password" if you cannot remember your password.',
        });
        return null;
      }
      const profile = await getDoc(doc(db, "members", existing.uid));
      if (profile.exists()) {
        clearDraft();
        toast.info("You are already registered. We have signed you in to your existing account.");
        navigate(`/profile/${existing.uid}`, { replace: true });
        return null;
      }
      return existing;
    }
  };

  const showDuplicate = (match) => {
    setDuplicate(match);
    setVerifyMessage(match.message);
    setVerifyStage("duplicate");
    setVerifyOpen(true);
  };

  /** Final button: validate everything, then open the facial recognition check. */
  const onFinish = () => {
    if (submittingRef.current) return;
    if (!imagePreview) { toast.error("Please take or upload your passport photograph"); setStep(0); return; }
    for (let i = 0; i < STEPS.length; i++) {
      if (!validateStepFields(stepFields[STEPS[i].key])) { setStep(i); return; }
    }
    setDuplicate(null);
    setVerifyMessage("");
    setVerifyCameraFailed(false);
    setVerifyStage("camera");
    setVerifyOpen(true);
  };

  const closeVerification = () => {
    if (["scanning", "checking", "creating"].includes(verifyStage)) return;
    setVerifyOpen(false);
  };

  /**
   * Scans the live face, then checks face (live + passport), email, phone and name
   * against every existing member. The account is only created if nothing matches.
   */
  const runFacialVerification = async () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setVerifyMessage("");
    setVerifyStage("scanning");
    try {
      let livePhoto = null;
      let liveDescriptor = null;
      if (!verifyCameraFailed) {
        const shot = verifyWebcamRef.current?.getScreenshot();
        if (!shot) throw Object.assign(new Error("Could not read the camera. Please try again."), { retry: true });
        livePhoto = await compressPhoto(shot, 640);
        const live = await analyseFace(livePhoto);
        if (!live.faces) {
          throw Object.assign(new Error("We could not see your face. Look straight at the camera in good light and try again."), { retry: true });
        }
        if (live.faces > 1) {
          throw Object.assign(new Error("More than one face was detected. Only the person registering should be in front of the camera."), { retry: true });
        }
        liveDescriptor = live.descriptor;
      }

      let passportDescriptor = null;
      try {
        const passport = await analyseFace(imagePreview);
        if (passport.faces >= 1) passportDescriptor = passport.descriptor;
      } catch (_) { }

      if (!liveDescriptor && !passportDescriptor) {
        submittingRef.current = false;
        setImagePreview(null);
        setVerifyOpen(false);
        setStep(0);
        toast.error("We could not see a face in your passport photo. Please take or upload a clear passport photo.");
        return;
      }

      setVerifyStage("checking");
      try {
        const result = await checkRegistration({
          stage: "final",
          email: formData.email?.trim(),
          phone_number: formData.phone_number,
          first_name: formData.first_name,
          last_name: formData.last_name,
          photo: livePhoto || (imagePreview.startsWith("data:") ? imagePreview : undefined),
          descriptor: liveDescriptor || passportDescriptor,
          descriptors: liveDescriptor && passportDescriptor ? [passportDescriptor] : [],
        });
        if (result.duplicate) {
          submittingRef.current = false;
          showDuplicate(result.duplicate);
          return;
        }
        if (result.warning) {
          pendingDescriptorRef.current = liveDescriptor || passportDescriptor;
          submittingRef.current = false;
          setVerifyMessage(result.warning.message);
          setVerifyStage("warning");
          return;
        }
      } catch (err) {
        if (err.status === 429) {
          throw Object.assign(new Error(err.message), { retry: false });
        }
        // Never block a genuine registration because the check service is unreachable;
        // the server re-checks and flags duplicates right after sign-up.
        console.warn("Registration check unavailable:", err);
      }

      setVerifyStage("creating");
      await completeSignup(liveDescriptor || passportDescriptor);
    } catch (err) {
      submittingRef.current = false;
      setVerifyMessage(err.message || "Something went wrong. Please try again.");
      setVerifyStage("error");
    }
  };

  const continueAfterWarning = async () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setVerifyStage("creating");
    await completeSignup(pendingDescriptorRef.current);
  };

  const completeSignup = async (faceDescriptor) => {
    const values = { ...formData };
    const userEmail = values.email?.trim();
    setLoading(true);
    try {
      const user = await createOrRecoverAccount(userEmail, values.password);
      if (!user) return;

      let profileImageUrl = imagePreview;
      if (imagePreview.startsWith("data:")) {
        const passportRef = ref(storage, `profile-images/${user.uid}.jpg`);
        await uploadBytes(passportRef, dataUrlToBlob(imagePreview), { contentType: "image/jpeg" });
        profileImageUrl = await getDownloadURL(passportRef);
      }

      let documentImageUrl = null;
      if (uploadedFile) {
        const sRef = ref(storage, `users/${user.uid}/document_image/${uploadedFile.name}`);
        await uploadBytes(sRef, uploadedFile);
        documentImageUrl = await getDownloadURL(sRef);
      }

      const userData = {
        uid: user.uid,
        ...values,
        is_priest: isPriest,
        email: userEmail,
        is_stakeholder: isStakeholder,
        profile_image: profileImageUrl,
        document_image: documentImageUrl,
        posted: isPriest ? values.posted : null,
        station_posted_to: isPriest && isPosted === "yes" ? values.station_posted_to : null,
        previous_stations: isPriest && isPosted === "yes" ? values.previous_stations : null,
        qualifications: isPriest && qualifications === "yes" ? values.qualificationsList : null,
      };

      // Never store passwords (or confirm fields) in Firestore
      delete userData.password;
      delete userData.confirmPassword;
      delete userData.confirm_password;

      const filtered = Object.fromEntries(
        Object.entries(userData).filter(([_, v]) => v !== undefined && v !== null)
      );
      await setDoc(doc(db, "members", user.uid), filtered);

      // Public safe mirror for /profile/{uid} (no email/phone/address/docs)
      await setDoc(
        doc(db, "public_member_profiles", user.uid),
        {
          uid: user.uid,
          first_name: filtered.first_name || null,
          last_name: filtered.last_name || null,
          middle_name: filtered.middle_name || null,
          pew: filtered.pew || null,
          office: filtered.office || null,
          officer_at: filtered.officer_at || null,
          profile_image: filtered.profile_image || null,
          updated_at: new Date().toISOString(),
        },
        { merge: true }
      );

      try {
        await indexMemberFace(await user.getIdToken(), faceDescriptor || undefined);
      } catch (err) {
        console.warn("Face indexing deferred:", err);
      }

      clearDraft();
      toast.success("Registration successful!");
      navigate(`/profile/${user.uid}`, { replace: true });
    } catch (error) {
      setVerifyOpen(false);
      if (error.code === "auth/network-request-failed") {
        toast.error("Network problem — please check your connection and press Complete Registration again. You will not be registered twice.");
      } else {
        toast.error("Error: " + error.message);
      }
    } finally {
      submittingRef.current = false;
      setLoading(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    await onFinish();
  };

  const progress = (step / (STEPS.length - 1)) * 100;

  return (
    <div className="min-h-screen bg-[#fff] pt-32 pb-20 px-4">
      <div className="max-w-2xl mx-auto">

        {/* ── Header ── */}
        <div className="text-center mb-10">
          <div className="flex items-center justify-center gap-2 mb-4">
            <span className="block w-7 h-px bg-blue-600" />
            <span className="text-blue-600 text-[10px] font-bold tracking-widest uppercase">BCS Member Portal</span>
            <span className="block w-7 h-px bg-blue-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Create Your Account</h1>
          <p className="text-gray-400 text-sm mt-1">Register your biodata</p>
        </div>

        {/* ── Progress bar ── */}
        <div className="mb-8">
          <div className="w-full h-0.5 bg-gray-200 rounded-full mb-6 relative">
            <div
              className="h-0.5 bg-blue-600 rounded-full transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="flex items-start justify-between">
            {STEPS.map((s, i) => (
              <div key={s.key} className="flex flex-col items-center gap-1.5 flex-1">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-black border-2 transition-all duration-300 ${i < step ? "bg-blue-600 border-blue-600 text-white"
                  : i === step ? "bg-white border-blue-600 text-blue-600"
                    : "bg-white border-gray-200 text-gray-300"
                  }`}>
                  {i < step ? (
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={3} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  ) : String(i + 1)}
                </div>
                <p className={`text-[9px] font-bold tracking-widest uppercase text-center hidden sm:block transition-colors ${i === step ? "text-blue-600" : "text-gray-300"}`}>
                  {s.label}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* ── Card ── */}
        <div className="bg-white border border-gray-100 rounded-sm px-6 md:px-10 py-8">

          <div className="mb-8 pb-5 border-b border-gray-100">
            <p className="text-[10px] font-black tracking-[0.3em] text-blue-600 uppercase">
              Step {step + 1} of {STEPS.length}
            </p>
            <h2 className="text-lg font-bold text-gray-900 mt-0.5">{STEPS[step].label}</h2>
            <p className="text-xs text-gray-400 mt-0.5">{STEPS[step].desc}</p>
          </div>

          {duplicate && (
            <div className="mb-8 border border-amber-300 bg-amber-50 rounded-sm p-5" role="alert">
              <p className="text-[10px] font-black tracking-[0.3em] text-amber-700 uppercase">Already Registered</p>
              <p className="text-sm text-amber-900 mt-2">{duplicate.message}</p>
              <div className="flex flex-wrap gap-3 mt-4">
                <Link
                  to="/login"
                  className="text-xs font-bold tracking-widest uppercase bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-sm"
                >
                  Go to Log In
                </Link>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit}>

            {/* ══ STEP 0: Account ══ */}
            <div style={{ display: step === 0 ? 'block' : 'none' }}>
              <div className="space-y-1">
                <div className="flex flex-col items-center mb-8">
                  <div className="relative mx-auto h-[7rem] w-[7rem] shrink-0">
                    <div
                      className={`absolute left-1/2 top-1/2 h-24 w-24 -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-full border-2 bg-gray-50 bg-cover bg-center bg-no-repeat ${imagePreview ? "border-blue-400" : "border-dashed border-gray-300"}`}
                      style={{
                        backgroundImage: `url(${JSON.stringify(imagePreview || DEFAULT_PROFILE_IMAGE)})`,
                      }}
                      role="img"
                      aria-label="Profile preview"
                    />
                    <button
                      type="button"
                      onClick={() => setCameraOpen(true)}
                      className="absolute bottom-1 right-1 z-10 flex h-8 w-8 items-center justify-center rounded-full border-2 border-white bg-blue-600 hover:bg-blue-700 transition-colors"
                    >
                      <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M4 7h4l2-2h4l2 2h4v12H4V7z" />
                        <circle cx="12" cy="13" r="3" />
                      </svg>
                    </button>
                  </div>
                  <div className="flex flex-wrap items-center justify-center gap-2 mt-4">
                    <button
                      type="button"
                      onClick={() => setCameraOpen(true)}
                      disabled={photoBusy}
                      className="text-[11px] font-bold tracking-widest uppercase border border-blue-200 text-blue-600 hover:bg-blue-50 px-4 py-2 rounded-sm disabled:opacity-50"
                    >
                      Take Photo
                    </button>
                    <button
                      type="button"
                      onClick={() => passportInputRef.current?.click()}
                      disabled={photoBusy}
                      className="text-[11px] font-bold tracking-widest uppercase border border-gray-200 text-gray-600 hover:bg-gray-50 px-4 py-2 rounded-sm disabled:opacity-50"
                    >
                      Upload Passport
                    </button>
                    <input
                      ref={passportInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handlePassportFile}
                    />
                  </div>
                  <p className="text-xs text-gray-400 mt-3 text-center">
                    Use a clear, recent passport photo showing only your face
                  </p>
                  {photoBusy && (
                    <p className="text-xs text-blue-500 font-semibold mt-1">Preparing photo…</p>
                  )}
                  {!imagePreview && !photoBusy && (
                    <p className="text-xs text-red-400 font-semibold mt-1">
                      * Photo is required to continue
                    </p>
                  )}
                  {imagePreview && (
                    <p className="text-xs text-green-500 font-semibold mt-1 flex items-center gap-1">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={3} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                      Photo captured
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-1 gap-x-8">
                  <Field name="email" label="Email Address">
                    <input
                      id="email"
                      type="email"
                      value={formData.email || ""}
                      onChange={(e) => updateField("email", e.target.value)}
                      className={inputCls}
                      placeholder="you@example.com"
                    />
                  </Field>
                  <Field name="password" label="Password">
                    <input
                      id="password"
                      type="password"
                      value={formData.password || ""}
                      onChange={(e) => updateField("password", e.target.value)}
                      className={inputCls}
                      placeholder="Create a strong password"
                    />
                  </Field>
                </div>

                <div className="pt-4">
                  <p className="text-xs text-gray-400 text-center">
                    Already have an account?{" "}
                    <Link to="/login" className="text-blue-600 font-semibold hover:text-blue-700">Sign In</Link>
                  </p>
                </div>
              </div>
            </div>

            {/* ══ STEP 1: Personal ══ */}
            <div style={{ display: step === 1 ? 'block' : 'none' }}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8">
                <Field name="first_name" label="First Name">
                  <input
                    id="first_name"
                    className={inputCls}
                    value={formData.first_name || ""}
                    onChange={(e) => updateField("first_name", e.target.value)}
                    placeholder="First name"
                  />
                </Field>
                <Field name="last_name" label="Last Name">
                  <input
                    id="last_name"
                    className={inputCls}
                    value={formData.last_name || ""}
                    onChange={(e) => updateField("last_name", e.target.value)}
                    placeholder="Last name"
                  />
                </Field>
                <Field name="phone_number" label="Phone Number">
                  <input
                    id="phone_number"
                    type="tel"
                    className={inputCls}
                    value={formData.phone_number || ""}
                    onChange={(e) => updateField("phone_number", e.target.value)}
                    placeholder="+234..."
                  />
                </Field>
                <Field name="next_of_kin1" label="Next of Kin">
                  <input
                    id="next_of_kin1"
                    className={inputCls}
                    value={formData.next_of_kin1 || ""}
                    onChange={(e) => updateField("next_of_kin1", e.target.value)}
                    placeholder="Full name of next of kin"
                  />
                </Field>
                <Field name="gender" label="Gender">
                  <select
                    id="gender"
                    className={selectCls}
                    value={formData.gender || ""}
                    onChange={(e) => updateField("gender", e.target.value)}
                  >
                    <option value="">Select gender</option>
                    <option value="brother">Brother</option>
                    <option value="sister">Sister</option>
                  </select>
                </Field>
                <Field name="pew" label="Pew">
                  <select
                    id="pew"
                    className={selectCls}
                    value={formData.pew || ""}
                    onChange={(e) => {
                      updateField("pew", e.target.value);
                      setIsMember(e.target.value !== "Non Member");
                    }}
                  >
                    <option value="">Select pew</option>
                    <option value="Non Member">Not Applicable</option>
                    <option value="Sabbath">Sabbath</option>
                    <option value="Congregation">Congregation</option>
                    <option value="Chorister">Chorister</option>
                    <option value="Spirited Child">Spirited Child</option>
                    <option value="Elder">Elder</option>
                    <option value="Ordained One">Ordained One</option>
                    <option value="Christ Natural Preacher">Christ Natural Preacher</option>
                    <option value="Christ Practical Student">Christ Practical Student</option>
                    <option value="Senior Christ Servant">Senior Christ Servant</option>
                    <option value="True Christ Witness">True Christ Witness</option>
                    <option value="Blessed Mother">Blessed Mother</option>
                    <option value="Divine Vanguard">Divine Vanguard</option>
                    <option value="Bishop">Bishop</option>
                    <option value="Christ Ambassador">Christ Ambassador</option>
                    <option value="Eminence">Eminence</option>
                    <option value="Archbishop">Archbishop</option>
                    <option value="Christ Shepherd">Christ Shepherd</option>
                    <option value="144,000 Virgin">144,000 Virgin</option>
                  </select>
                </Field>
              </div>
            </div>

            {/* ══ STEP 2: Location ══ */}
            <div style={{ display: step === 2 ? 'block' : 'none' }}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8">
                <Field name="country_of_residence" label="Country of Residence">
                  <select
                    id="country_of_residence"
                    className={selectCls}
                    value={formData.country_of_residence || ""}
                    onChange={(e) => updateField("country_of_residence", e.target.value)}
                  >
                    <option value="">Select country</option>
                    {countryOptions.map(country => <option key={country} value={country}>{country}</option>)}
                  </select>
                </Field>
                <Field name="country_of_origin" label="Country of Origin">
                  <select
                    id="country_of_origin"
                    className={selectCls}
                    value={formData.country_of_origin || ""}
                    onChange={(e) => updateField("country_of_origin", e.target.value)}
                  >
                    <option value="">Select country</option>
                    {countryOptions.map(country => <option key={country} value={country}>{country}</option>)}
                  </select>
                </Field>
                <Field name="residing_state" label="State of Residence">
                  {formData.country_of_residence === "Nigeria" ? (
                    <select
                      id="residing_state"
                      className={selectCls}
                      value={formData.residing_state || ""}
                      onChange={(e) => updateField("residing_state", e.target.value)}
                    >
                      <option value="">Select state</option>
                      {states && states.map(state => <option key={state.code} value={state.name}>{state.name}</option>)}
                    </select>
                  ) : (
                    <input
                      id="residing_state"
                      className={inputCls}
                      value={formData.residing_state || ""}
                      onChange={(e) => updateField("residing_state", e.target.value)}
                      placeholder="e.g. Lagos"
                    />
                  )}
                </Field>
                <Field name="residential_address" label="Residential Address">
                  <input
                    id="residential_address"
                    className={inputCls}
                    value={formData.residential_address || ""}
                    onChange={(e) => updateField("residential_address", e.target.value)}
                    placeholder="Street address"
                  />
                </Field>
                <Field name="state_of_origin" label="State of Origin">
                  {formData.country_of_origin === "Nigeria" ? (
                    <select
                      id="state_of_origin"
                      className={selectCls}
                      value={formData.state_of_origin || ""}
                      onChange={(e) => {
                        updateField("state_of_origin", e.target.value);
                        // Clear LGA when state changes
                        updateField("lga_of_origin", "");
                      }}
                    >
                      <option value="">Select state</option>
                      {states && states.map(state => <option key={state.code} value={state.name}>{state.name}</option>)}
                    </select>
                  ) : (
                    <input
                      id="state_of_origin"
                      className={inputCls}
                      value={formData.state_of_origin || ""}
                      onChange={(e) => updateField("state_of_origin", e.target.value)}
                      placeholder="e.g. Cross River"
                    />
                  )}
                </Field>
                <Field name="lga_of_origin" label="LGA / Province / County">
                  {formData.country_of_origin === "Nigeria" && formData.state_of_origin ? (
                    <select
                      id="lga_of_origin"
                      className={selectCls}
                      value={formData.lga_of_origin || ""}
                      onChange={(e) => updateField("lga_of_origin", e.target.value)}
                    >
                      <option value="">Select LGA</option>
                      {(() => {
                        const lgas = getSafeLgas(formData.state_of_origin);
                        return lgas.length > 0 ? lgas.map(lga => <option key={lga} value={lga}>{lga}</option>) : null;
                      })()}
                    </select>
                  ) : (
                    <input
                      id="lga_of_origin"
                      className={inputCls}
                      value={formData.lga_of_origin || ""}
                      onChange={(e) => updateField("lga_of_origin", e.target.value)}
                      placeholder="LGA or county"
                    />
                  )}
                </Field>
                <Field name="village" label="Village / Hometown">
                  <input
                    id="village"
                    className={inputCls}
                    value={formData.village || ""}
                    onChange={(e) => updateField("village", e.target.value)}
                    placeholder="Your village"
                  />
                </Field>
              </div>
            </div>

            {/* ══ STEP 3: Bethel ══ */}
            <div style={{ display: step === 3 ? 'block' : 'none' }}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8">
                {isMember ? (
                  <>
                    <Field name="nation_of_worship" label="Nation of Worship">
                      <input
                        id="nation_of_worship"
                        className={inputCls}
                        value={formData.nation_of_worship || ""}
                        onChange={(e) => updateField("nation_of_worship", e.target.value)}
                        placeholder="Your nation of worship"
                      />
                    </Field>
                    <Field name="bethel" label="Bethel Name">
                      <input
                        id="bethel"
                        className={inputCls}
                        value={formData.bethel || ""}
                        onChange={(e) => updateField("bethel", e.target.value)}
                        placeholder="Your Bethel"
                      />
                    </Field>
                    <Field name="bethel_Location" label="Bethel Address">
                      <input
                        id="bethel_Location"
                        className={inputCls}
                        value={formData.bethel_Location || ""}
                        onChange={(e) => updateField("bethel_Location", e.target.value)}
                        placeholder="Bethel street address"
                      />
                    </Field>
                    <Field name="bethel_country" label="Bethel Country">
                      <select
                        id="bethel_country"
                        className={selectCls}
                        value={formData.bethel_country || ""}
                        onChange={(e) => updateField("bethel_country", e.target.value)}
                      >
                        <option value="">Select country</option>
                        {countryOptions.map(country => <option key={country} value={country}>{country}</option>)}
                      </select>
                    </Field>
                  </>
                ) : (
                  <div className="col-span-2 py-10 text-center">
                    <p className="text-gray-400 text-sm">No Bethel details required for non-members.</p>
                  </div>
                )}
              </div>
            </div>

            {/* ══ STEP 4: Priesthood ══ */}
            <div style={{ display: step === 4 ? 'block' : 'none' }}>
              <div className="space-y-6">

                {/* Priest toggle */}
                <div className="flex items-center justify-between py-4 border-b border-gray-100">
                  <div>
                    <p className="text-xs font-bold text-gray-700">Are you a Priest?</p>
                    <p className="text-xs text-gray-400 mt-0.5">Toggle if you hold a priesthood rank</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setIsPriest(!isPriest)}
                    className={`relative w-11 h-6 rounded-full transition-colors duration-300 ${isPriest ? "bg-blue-600" : "bg-gray-200"}`}
                  >
                    <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform duration-300 ${isPriest ? "translate-x-5" : ""}`} />
                  </button>
                </div>

                <div style={{ display: isPriest ? 'block' : 'none' }}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8">
                    <Field name="pew_position" label="Position in Priesthood" required={false}>
                      <input
                        id="pew_position"
                        className={inputCls}
                        value={formData.pew_position || ""}
                        onChange={(e) => updateField("pew_position", e.target.value)}
                        placeholder="e.g. Christ Shepherd"
                      />
                    </Field>
                    <Field name="next_of_kin" label="Next of Kin Contact" required={false}>
                      <input
                        id="next_of_kin"
                        className={inputCls}
                        value={formData.next_of_kin || ""}
                        onChange={(e) => updateField("next_of_kin", e.target.value)}
                        placeholder="Phone number"
                      />
                    </Field>
                    <Field name="posted" label="Are you Posted?" required={false}>
                      <select
                        id="posted"
                        className={selectCls}
                        value={formData.posted || ""}
                        onChange={(e) => updateField("posted", e.target.value)}
                      >
                        <option value="">Select</option>
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                      </select>
                    </Field>
                    <Field name="qualifications" label="Do you have Qualifications?" required={false}>
                      <select
                        id="qualifications"
                        className={selectCls}
                        value={formData.qualifications || ""}
                        onChange={(e) => updateField("qualifications", e.target.value)}
                      >
                        <option value="">Select</option>
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                      </select>
                    </Field>
                    <div style={{ display: formData.posted === "yes" ? 'block' : 'none' }} className="col-span-1 sm:col-span-2">
                      <Field name="station_posted_to" label="Station Posted To" required={false}>
                        <input
                          id="station_posted_to"
                          className={inputCls}
                          value={formData.station_posted_to || ""}
                          onChange={(e) => updateField("station_posted_to", e.target.value)}
                          placeholder="Current station"
                        />
                      </Field>
                      <Field name="previous_stations" label="Previous Stations" required={false}>
                        <input
                          id="previous_stations"
                          className={inputCls}
                          value={formData.previous_stations || ""}
                          onChange={(e) => updateField("previous_stations", e.target.value)}
                          placeholder="Previous postings"
                        />
                      </Field>
                    </div>
                    <div style={{ display: formData.qualifications === "yes" ? 'block' : 'none' }} className="col-span-1 sm:col-span-2">
                      <Field name="qualificationsList" label="List Qualifications" span={2} required={false}>
                        <input
                          id="qualificationsList"
                          className={inputCls}
                          value={formData.qualificationsList || ""}
                          onChange={(e) => updateField("qualificationsList", e.target.value)}
                          placeholder="e.g. WAEC, BSc, Masters..."
                        />
                      </Field>
                    </div>
                  </div>
                </div>

                {/* Stakeholder toggle */}
                <div className="flex items-center justify-between py-4 border-b border-gray-100">
                  <div>
                    <p className="text-xs font-bold text-gray-700">Office Holder / Staff</p>
                    <p className="text-xs text-gray-400 mt-0.5">BCS Establishment, Department or Enterprise</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setIsStakeholder(!isStakeholder)}
                    className={`relative w-11 h-6 rounded-full transition-colors duration-300 ${isStakeholder ? "bg-blue-600" : "bg-gray-200"}`}
                  >
                    <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform duration-300 ${isStakeholder ? "translate-x-5" : ""}`} />
                  </button>
                </div>

                <div style={{ display: isStakeholder ? 'block' : 'none' }}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8">
                    <Field name="officer_at" label="Formation Level" required={false}>
                      <select
                        id="officer_at"
                        className={selectCls}
                        value={formData.officer_at || ""}
                        onChange={(e) => updateField("officer_at", e.target.value)}
                      >
                        <option value="">Select formation</option>
                        {categories.map((c) => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                    </Field>
                    <Field name="year_assumed" label="Date of Appointment" required={false}>
                      <input
                        id="year_assumed"
                        type="date"
                        className={inputCls}
                        value={formData.year_assumed || ""}
                        onChange={(e) => updateField("year_assumed", e.target.value)}
                      />
                    </Field>
                    <Field name="position" label="Department / Formation" required={false}>
                      <input
                        id="position"
                        list="departmentOptions"
                        className={inputCls}
                        value={formData.position || ""}
                        onChange={(e) => updateField("position", e.target.value)}
                        placeholder="Select or type"
                      />
                    </Field>
                    <Field name="office" label="Your Position in Office" required={false}>
                      <input
                        id="office"
                        className={inputCls}
                        value={formData.office || ""}
                        onChange={(e) => updateField("office", e.target.value)}
                        placeholder="Your title or role"
                      />
                    </Field>
                    <div className="col-span-1 sm:col-span-2">
                      <Field name="means_of_identification" label="Means of Identification" required={false}>
                        <div className="flex flex-col gap-2 mt-1 text-sm text-gray-600">
                          <label className="inline-flex items-center gap-2">
                            <input
                              type="radio"
                              name="means_of_identification"
                              value="nin"
                              checked={formData.means_of_identification === "nin"}
                              onChange={(e) => updateField("means_of_identification", e.target.value)}
                            />
                            National ID / NIN (Nigerians)
                          </label>
                          <label className="inline-flex items-center gap-2">
                            <input
                              type="radio"
                              name="means_of_identification"
                              value="drivers_license"
                              checked={formData.means_of_identification === "drivers_license"}
                              onChange={(e) => updateField("means_of_identification", e.target.value)}
                            />
                            Driver's Licence
                          </label>
                          <label className="inline-flex items-center gap-2">
                            <input
                              type="radio"
                              name="means_of_identification"
                              value="international_passport_id"
                              checked={formData.means_of_identification === "international_passport_id"}
                              onChange={(e) => updateField("means_of_identification", e.target.value)}
                            />
                            International Passport
                          </label>
                        </div>
                      </Field>
                    </div>
                    <div className="col-span-1 sm:col-span-2">
                      <Field name="document_image" label="Upload ID Document" required={false}>
                        <label className="flex items-center gap-2 border border-gray-300 text-gray-600 text-xs font-semibold px-4 py-2.5 rounded-sm cursor-pointer hover:border-blue-400 hover:text-blue-600 transition-all">
                          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M4 16v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
                            <path d="M12 12v9" />
                            <path d="M8 16l4-4 4 4" />
                            <path d="M12 2v10" />
                          </svg>
                          Upload Document
                          <input type="file" accept="image/*" onChange={handleDocumentUpload} className="hidden" />
                        </label>
                        {formData.document_image && <p className="text-xs text-gray-500 mt-2">{formData.document_image}</p>}
                      </Field>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ── Navigation ── */}
            <div className={`flex mt-10 pt-6 border-t border-gray-100 ${step > 0 ? "justify-between" : "justify-end"}`}>
              {step > 0 && (
                <button
                  type="button"
                  onClick={prevStep}
                  className="flex items-center gap-2 border border-gray-200 text-gray-500 hover:bg-gray-50 text-xs font-bold tracking-widest uppercase px-6 py-3 rounded-sm transition-all duration-200"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M7 16l-4-4m0 0l4-4m-4 4h18" />
                  </svg>
                  Back
                </button>
              )}

              {step < STEPS.length - 1 ? (
                <button
                  type="button"
                  onClick={nextStep}
                  disabled={photoBusy}
                  className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold tracking-widest uppercase px-8 py-3 rounded-sm transition-all duration-200 disabled:opacity-50"
                >
                  Continue
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={loading || verifyOpen}
                  className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold tracking-widest uppercase px-8 py-3 rounded-sm transition-all duration-200 disabled:opacity-50"
                >
                  {loading || verifyOpen ? "Verifying..." : "Complete Registration"}
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 5v14m-7-7h14" />
                  </svg>
                </button>
              )}
            </div>
          </form>
        </div>

        {/* Draft notice */}
        <p className="text-center text-xs text-gray-400 mt-4">
          Your progress is saved automatically. Refreshing won't lose your data.
        </p>
      </div>

      {/* Facial recognition — final step before the account is created */}
      {verifyOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <div className="w-full max-w-md bg-white rounded-sm p-6 shadow-xl">
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="text-[10px] font-black tracking-[0.3em] text-blue-600 uppercase">Final Step</p>
                <h2 className="text-base font-bold text-gray-900 mt-0.5">Facial Recognition</h2>
                <p className="text-xs text-gray-500 mt-1">
                  We confirm you are not already registered by checking your face, email, phone number and details.
                </p>
              </div>
              {!["scanning", "checking", "creating"].includes(verifyStage) && (
                <button type="button" onClick={closeVerification} className="text-xs text-gray-500 hover:text-gray-700 ml-4">
                  Close
                </button>
              )}
            </div>

            {["camera", "scanning", "error"].includes(verifyStage) && (
              <>
                {!verifyCameraFailed ? (
                  <div className="relative">
                    <Webcam
                      audio={false}
                      ref={verifyWebcamRef}
                      screenshotFormat="image/jpeg"
                      screenshotQuality={0.92}
                      videoConstraints={{ facingMode: "user", width: { ideal: 1280 }, height: { ideal: 960 } }}
                      onUserMediaError={() => setVerifyCameraFailed(true)}
                      className="w-full rounded-sm bg-gray-100"
                      mirrored
                    />
                    <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                      <div className={`w-1/2 aspect-[3/4] rounded-[50%] border-4 ${verifyStage === "scanning" ? "border-blue-500 animate-pulse" : "border-white/80"}`} />
                    </div>
                  </div>
                ) : (
                  <div className="border border-dashed border-gray-300 rounded-sm p-5 text-center">
                    <p className="text-sm text-gray-700">Your camera is not available.</p>
                    <p className="text-xs text-gray-500 mt-1">
                      Allow camera access and reopen this window, or continue and we will verify you using your passport photo.
                    </p>
                  </div>
                )}

                {verifyStage === "error" && verifyMessage && (
                  <p className="text-xs text-red-500 font-semibold mt-3">{verifyMessage}</p>
                )}

                <p className="text-xs text-gray-500 mt-3">
                  {verifyCameraFailed
                    ? "Your passport photo will be compared with existing members."
                    : "Position your face inside the oval, look straight at the camera, then press Scan My Face."}
                </p>

                <button
                  type="button"
                  onClick={runFacialVerification}
                  disabled={verifyStage === "scanning"}
                  className="w-full mt-4 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold tracking-widest uppercase py-3 rounded-sm transition-all duration-200 disabled:opacity-60"
                >
                  {verifyStage === "scanning"
                    ? "Scanning your face…"
                    : verifyCameraFailed
                      ? "Verify With Passport Photo"
                      : verifyStage === "error" ? "Try Again" : "Scan My Face"}
                </button>
              </>
            )}

            {verifyStage === "checking" && (
              <div className="py-10 text-center">
                <div className="mx-auto h-10 w-10 rounded-full border-4 border-blue-100 border-t-blue-600 animate-spin" />
                <p className="text-sm font-semibold text-gray-800 mt-4">Checking our member records…</p>
                <p className="text-xs text-gray-500 mt-1">Face, email, phone number and name</p>
              </div>
            )}

            {verifyStage === "creating" && (
              <div className="py-10 text-center">
                <div className="mx-auto h-10 w-10 rounded-full border-4 border-green-100 border-t-green-600 animate-spin" />
                <p className="text-sm font-semibold text-green-700 mt-4">Verified — you are not already registered</p>
                <p className="text-xs text-gray-500 mt-1">Creating your account, please don't close this page…</p>
              </div>
            )}

            {verifyStage === "warning" && (
              <div className="border border-amber-300 bg-amber-50 rounded-sm p-5" role="alert">
                <p className="text-[10px] font-black tracking-[0.3em] text-amber-700 uppercase">Phone Number Already In Use</p>
                <p className="text-sm text-amber-900 mt-2">{verifyMessage}</p>
                <div className="flex flex-wrap gap-3 mt-4">
                  <button
                    type="button"
                    onClick={continueAfterWarning}
                    className="text-xs font-bold tracking-widest uppercase bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-sm"
                  >
                    Continue Registration
                  </button>
                  <Link
                    to="/login"
                    className="text-xs font-bold tracking-widest uppercase border border-gray-300 text-gray-700 hover:bg-white px-5 py-2.5 rounded-sm"
                  >
                    Log In Instead
                  </Link>
                </div>
              </div>
            )}

            {verifyStage === "duplicate" && (
              <div className="border border-amber-300 bg-amber-50 rounded-sm p-5" role="alert">
                <p className="text-[10px] font-black tracking-[0.3em] text-amber-700 uppercase">Already Registered</p>
                <p className="text-sm text-amber-900 mt-2">{verifyMessage}</p>
                <div className="flex flex-wrap gap-3 mt-4">
                  <Link
                    to="/login"
                    className="text-xs font-bold tracking-widest uppercase bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-sm"
                  >
                    Go to Log In
                  </Link>
                  <button
                    type="button"
                    onClick={() => setVerifyOpen(false)}
                    className="text-xs font-bold tracking-widest uppercase border border-gray-300 text-gray-700 hover:bg-white px-5 py-2.5 rounded-sm"
                  >
                    Edit My Details
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Camera overlay */}
      {cameraOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-md bg-white rounded-sm p-5 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-bold">Take Your Passport Photo</h2>
              <button type="button" onClick={() => setCameraOpen(false)} className="text-gray-500 hover:text-gray-700">
                Close
              </button>
            </div>
            <Webcam
              audio={false}
              ref={webcamRef}
              screenshotFormat="image/jpeg"
              screenshotQuality={0.92}
              videoConstraints={{ facingMode: "user", width: { ideal: 1280 }, height: { ideal: 960 } }}
              onUserMediaError={() => {
                setCameraOpen(false);
                toast.error("Camera is not available. Please allow camera access or use Upload Passport instead.");
              }}
              className="w-full rounded-sm"
              mirrored
            />
            <button
              type="button"
              onClick={capturePhoto}
              className="w-full mt-3 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold tracking-widest uppercase py-3 rounded-sm transition-all duration-200"
            >
              Capture Photo
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Register;